import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { CustomerDetail } from '../../../_models/customer.model';
import { AdminContactService } from '../../../_services/api/admin-contact.service';
import { Contact } from '../../../_models/contact.model';

@Component({
  selector: 'app-admin-contact-details',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-contact-details.html',
  styleUrls: ['./admin-contact-details.scss']
})
export class AdminContactDetails implements OnInit {
  contactId!: number;
  contact: Contact | null = null;
  isLoading = false;
  errorMessage: string | null = null;

  menuOpen = false;
  showDeleteModal = false;
  isUpdatingVisibility = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contactService: AdminContactService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {    
    this.loadContactId();
    if(this.contactId){
        this.menuStateService.activeMenu$.subscribe(activeId => {
            this.menuOpen = activeId === this.contactId;
        });
        if (isPlatformBrowser(this.platformId)) {
            document.addEventListener('click', this.closeMenu.bind(this));
        }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.contactId);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  private loadContactId(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.contactId = +id;
        this.loadContactDetails();
      } else {
        this.errorMessage = 'ID du contact non trouvé';
        this.goToList();
      }
    });
  }

  private loadContactDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.contactService.getContactDetails(this.contactId).subscribe({
      next: (response: ApiResponse<Contact>) => {
        if (response.success && response.data) {
          this.contact = response.data;
        } else {
          this.errorMessage = response.message || 'Erreur lors du chargement des données';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading demande de contact details:', error);
        this.errorMessage = 'Erreur de chargement des détails du demande de contact';
        this.isLoading = false;
        if (error.status === 404) {
          this.errorMessage = 'demande de contact non trouvé';
          setTimeout(() => this.goToList(), 2000);
        }
      }
    });
  }

  goToList(): void {
    this.router.navigate(['/admin/contacts']);
  }  

  toggleVisibility(): void { 
    if(!this.contact) return;
    if (this.isUpdatingVisibility) return;
  
    this.isUpdatingVisibility = true;
    this.menuStateService.setActiveMenu(null);
    this.menuOpen = false;
    
    if (this.contact.isRead) {
      this.contactService.markAsUnread(this.contact.id).subscribe({
        next: () => {
          this.contact!.isRead = false;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le message a été marqué comme non lu.");
        },
        error: (error) => {
          console.error('Error making contact as no read:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError("Une erreur est survenue. Veuillez réessayer.");
        }
      });
    } else {
      this.contactService.markAsRead(this.contact.id).subscribe({
        next: () => {
          this.contact!.isRead = true;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le message a été marqué comme lu.");
        },
        error: (error) => {
          console.error('Error making contact requets as read:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError("Une erreur est survenue. Veuillez réessayer.");
        }
      });
    }
  }

  toggleArchive(): void {
    if(!this.contact) return;
    if (this.isUpdatingVisibility) return;
  
    this.isUpdatingVisibility = true;
    this.menuStateService.setActiveMenu(null);
    this.menuOpen = false;
    
    if (this.contact.isArchived) {
      this.contactService.unarchiveContact(this.contact.id).subscribe({
        next: () => {
          this.contact!.archivedAt = 'false';
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le message a été retiré des archives.");
        },
        error: (error) => {
          console.error('Error making contact as no read:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError("Impossible de modifier l’archive. Réessayez.");
        }
      });
    } else {
      this.contactService.archiveContact(this.contact.id).subscribe({
        next: () => {
          this.contact!.archivedAt = 'true';
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le message a été désarchivé.");
        },
        error: (error) => {
          console.error('Error making contact requets as read:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError("Impossible de modifier l’archive. Réessayez.");
        }
      });
    }
  }

  confirmDelete(): void {
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  deleteContact(): void {
    this.contactService.deleteContact(this.contact!.id).subscribe({
      next: () => {
        this.showDeleteModal = false;
        this.goToList();
      },
      error: (error) => {
        console.error('Error deleting contact:', error);
        this.notification.showError('Erreur lors de la suppression du demande de contact');
        this.showDeleteModal = false;
      }
    });
  }
}